import { ColumnMeta, FilterResultMethod } from '@api/api/screener';
import {
  formatScreenerCellValue,
  isScreenerPercentageColumn,
  mapValueTypeToFormatter,
} from '@api/utils/watchlist-table-formatters';
import { WithNAFallback } from '@design/components/table';
import {
  getPriceStatus,
  PriceStatus,
  Status,
} from '@helpers/utils/numbers/numbers';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { priceChangeColors } from '@terminal/utils/common/utils';
import { ReactNode } from 'react';

import { ColumnMetaWithKey } from '../results/screener-table-utils';
import styles from './ScreenerCell.module.scss';

interface ScreenerCellProps {
  value: FilterResultMethod;
  columnMeta: ColumnMetaWithKey | ColumnMeta;
  isDisabled?: boolean;
  customTooltip?: ReactNode;
}

export const ScreenerCell = ({
  value,
  columnMeta,
  isDisabled,
  customTooltip,
}: ScreenerCellProps) => {
  const unit = value.units?.toUpperCase() ?? '';
  const formatter = mapValueTypeToFormatter[columnMeta.value_type || 'integer'];
  const status = columnMeta.directional
    ? getPriceStatus(value.value)
    : PriceStatus.Default;
  const tooltipValue = customTooltip ?? value.tooltip;

  const isPercentage = isScreenerPercentageColumn(columnMeta.name);

  const color =
    priceChangeColors[isDisabled ? Status.Disabled : status] ?? undefined;

  return (
    <TooltipContainer
      className={styles.styledTooltip}
      disabled={!tooltipValue}
      inPortal
      label={tooltipValue}
      placement="top-end"
    >
      <span aria-haspopup={!!tooltipValue}>
        <WithNAFallback
          value={
            value.value !== undefined && (
              <div style={{ color }}>
                {isPercentage
                  ? formatScreenerCellValue(value, columnMeta)
                  : formatter(value.value)}
                {!isPercentage && unit && (
                  <span className={styles.unit}>{unit}</span>
                )}
              </div>
            )
          }
        />
      </span>
    </TooltipContainer>
  );
};
